import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn, timeAgo } from '@/lib/utils';
import {
  fetchPortalNotifications,
  markPortalNotificationRead,
  markAllPortalNotificationsRead,
} from '@/lib/queries';
import {
  PORTAL_NOTIFICATION_EVENT,
  usePortalRealtime,
} from '@/contexts/PortalRealtimeContext';
import type { PortalNotification } from '@/types/portal';

export function NotificationBell() {
  const navigate = useNavigate();
  const { unreadNotifications, refreshUnread } = usePortalRealtime();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(() => {
    setLoading(true);
    fetchPortalNotifications()
      .then(data => setNotifications(data.slice(0, 8)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) loadNotifications();
  }, [open, loadNotifications]);

  useEffect(() => {
    const onNew = (event: Event) => {
      const notification = (event as CustomEvent<PortalNotification>).detail;
      setNotifications(previous => {
        if (previous.some(item => item.id === notification.id)) return previous;
        return [notification, ...previous].slice(0, 8);
      });
    };
    window.addEventListener(PORTAL_NOTIFICATION_EVENT, onNew);
    return () => window.removeEventListener(PORTAL_NOTIFICATION_EVENT, onNew);
  }, []);

  const handleOpen = async (notification: PortalNotification) => {
    if (!notification.read_at) {
      try {
        await markPortalNotificationRead(notification.id);
        setNotifications(previous =>
          previous.map(item =>
            item.id === notification.id
              ? { ...item, read_at: new Date().toISOString() }
              : item
          )
        );
        refreshUnread();
      } catch {
        // non-blocking
      }
    }
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllPortalNotificationsRead();
      setNotifications(previous =>
        previous.map(item => ({
          ...item,
          read_at: item.read_at ?? new Date().toISOString(),
        }))
      );
      refreshUnread();
    } catch {
      // non-blocking
    }
  };

  const hasUnread = notifications.some(n => !n.read_at);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-white/50 hover:text-white/80"
          aria-label="Notifications"
        >
          <Bell className="size-4" />
          {unreadNotifications > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {unreadNotifications > 99 ? '99+' : unreadNotifications}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <p className="text-sm font-medium">Notifications</p>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleMarkAll}
            >
              <CheckCheck className="size-3.5" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : notifications.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No notifications yet
            </p>
          ) : (
            notifications.map(notification => {
              const unread = !notification.read_at;
              return (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => handleOpen(notification)}
                  className={cn(
                    'flex w-full items-start gap-2 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-accent/40',
                    unread && 'bg-accent/20'
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm', unread ? 'font-semibold' : 'font-medium')}>
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {notification.body}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {timeAgo(notification.created_at)}
                    </p>
                  </div>
                  {unread && (
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
        <div className="border-t p-2">
          <Button variant="ghost" size="sm" className="w-full" asChild>
            <Link to="/notifications" onClick={() => setOpen(false)}>
              View all notifications
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
